import {
  ExtraService,
  getBuildingById,
  getDefaultBuilding,
  getMeetingById,
  getUser,
  getWorkspaceReservationByDate,
  useAppSelector,
  useOverridableFeatureFlags,
} from '@lib/store';
import {getWorkdaysWithWorkspaceReservationsForDate} from '@lib/store/src/data/timeslots/selectors';
import {
  BASE_PREFILL_FIELDS,
  EVENT_BASED_PREFILL_FIELDS,
  GENERAL_BASED_PREFILL_FIELDS,
  LOCATION_BASED_PREFILL_FIELDS,
  WORKDAY_BASED_PREFILL_FIELDS,
} from '@lib/utils';
import {format, isWithinInterval, parseISO} from 'date-fns';
import {transformResourceToUrl} from '../../util';

const now = new Date();

export const useExtraServiceUrl = (
  service?: ExtraService,
  meetingId?: string,
  selectedDate?: string,
  locationName?: string,
) => {
  const {resource, category, buildingId} = service ?? {};
  const dateKey = format(selectedDate ? parseISO(selectedDate) : now, 'yyyy-MM-dd');

  const generalData = useGeneralPrefillFields(dateKey);
  const workdayData = useWorkdayPrefillFields(buildingId ?? '', dateKey);
  const eventData = useEventPrefillFields(buildingId ?? '', meetingId);
  const locationData = useLocationPrefillFields(buildingId ?? '', locationName);

  switch (category) {
    case 'General':
      return transformResourceToUrl(generalData, resource);
    case 'WorkdayBased':
      return transformResourceToUrl(workdayData, resource);
    case 'EventBased':
      return transformResourceToUrl(eventData, resource);
    case 'LocationBased':
      return transformResourceToUrl(locationData, resource);
    default:
      return '';
  }
};

const useGeneralPrefillFields = (
  dateKey: string,
): Record<(typeof GENERAL_BASED_PREFILL_FIELDS)[number], string | number | undefined> => {
  const defaultBuilding = useAppSelector(getDefaultBuilding);

  const baseProps = useBasePrefillFields();

  return {
    ...baseProps,
    defaultOffice: defaultBuilding?.name,
    dateOfThatDay: dateKey,
  };
};

const useWorkdayPrefillFields = (
  buildingId: string,
  dateKey: string,
): Record<(typeof WORKDAY_BASED_PREFILL_FIELDS)[number], string | number | undefined> => {
  const {TimeslotsApi} = useOverridableFeatureFlags();
  const building = useAppSelector((state) => getBuildingById(state, buildingId));

  const {workdays = []} = useAppSelector(getWorkdaysWithWorkspaceReservationsForDate(dateKey)) ?? {};
  const workspaceReservations_timeslots =
    workdays
      .find((wd) => wd.workday.status === 'OfficeDay' && wd.workday.buildingId === buildingId)
      ?.workspaceReservations.filter((wr) => wr.status === 'Confirmed') ?? [];
  const workspaceReservation_timeslots =
    workspaceReservations_timeslots.length <= 1
      ? workspaceReservations_timeslots[0]
      : workspaceReservations_timeslots.find((wr) =>
          isWithinInterval(now, {start: parseISO(wr.localStart), end: parseISO(wr.localEnd)}),
        );

  const workspaceReservation = useAppSelector((state) => getWorkspaceReservationByDate(state, parseISO(dateKey)));

  const baseProps = useBasePrefillFields();

  return {
    ...baseProps,
    officeBooked: building?.name,
    workspaceBooked: TimeslotsApi
      ? workspaceReservation_timeslots?.workspace.nodeName
      : workspaceReservation?.deskName ?? workspaceReservation?.areaName,
    date: format(parseISO(dateKey), 'yyyy-MM-dd'),
  };
};

const useEventPrefillFields = (
  buildingId: string,
  meetingId?: string,
): Record<(typeof EVENT_BASED_PREFILL_FIELDS)[number], string | number | undefined> => {
  const building = useAppSelector((state) => getBuildingById(state, buildingId));
  const meeting = useAppSelector((state) => getMeetingById(state, meetingId ?? ''));

  const baseProps = useBasePrefillFields();

  return {
    ...baseProps,
    officeBooked: building?.name,
    dateAndTimeEventPlanned: meeting?.startDateTime
      ? format(parseISO(meeting?.startDateTime), 'EEEE dd MMMM HH:mm')
      : undefined,
    numberOfInvitees: meeting?.attendees.length,
  };
};

const useLocationPrefillFields = (
  buildingId: string,
  locationName?: string,
): Record<(typeof LOCATION_BASED_PREFILL_FIELDS)[number], string | number | undefined> => {
  const {name: office} = useAppSelector((state) => getBuildingById(state, buildingId)) ?? {};

  const baseProps = useBasePrefillFields();

  return {
    ...baseProps,
    locationName,
    office,
  };
};

const useBasePrefillFields = (): Record<(typeof BASE_PREFILL_FIELDS)[number], string | number | undefined> => {
  const {firstName, lastName, email} = useAppSelector(getUser);
  const date = new Date();

  return {
    firstName,
    lastName,
    email,
    date: format(date, 'yyyy-MM-dd'),
    time: format(date, 'HH:mm'),
  };
};
